import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Avatar,
  IconButton,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  Divider,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  SelectChangeEvent,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import CreatePropertyDialog from "../../components/property/CreatePropertyForm";
import {
  useDeleteProperty,
  useGetAllProperties,
} from "../../hooks/query/useProperty";
import { AuthContext } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import FallbackLoader from "../../components/loading/FallBackLoader";
import HomeIcon from "@mui/icons-material/Home";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import { toast } from "react-toastify";

// Define property type
interface Property {
  id: string;
  createdAt: string;
  updatedAt: string;
  propertyName: string;
  address: string;
  latitude: string;
  longitude: string;
  tenant: {
    id: string;
    name: string;
  };
  vastuRemedy: {
    id: string;
    observations: any;
    recommendations: any;
    remedies: any;
  } | null;
}

type SortOption =
  | "name-asc"
  | "name-desc"
  | "date-asc"
  | "date-desc"
  | "hasRemedy";

const PropertyListingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { currentUser } = useContext(AuthContext);
  const { data, isLoading, error } = useGetAllProperties(
    currentUser?.tenant?.id
  );

  // State for properties
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState<SortOption>("date-desc");

  // Other states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const [editAnchorEl, setEditAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPropertyForEdit, setSelectedPropertyForEdit] =
    useState<Property | null>(null);

  const { mutateAsync: deletePropertyById } = useDeleteProperty();

  // Set properties when data is loaded
  useEffect(() => {
    if (data) {
      setProperties(data);
      setFilteredProperties(data);
    }
  }, [data]);

  // Filter and sort properties when search term or sort option changes
  useEffect(() => {
    if (!properties) return;

    // Filter properties based on search term
    const filtered = properties.filter(
      (property) =>
        property.propertyName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        property.address.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort filtered properties
    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case "name-asc":
          return a.propertyName.localeCompare(b.propertyName);
        case "name-desc":
          return b.propertyName.localeCompare(a.propertyName);
        case "date-asc":
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        case "date-desc":
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        case "hasRemedy":
          return (b.vastuRemedy ? 1 : 0) - (a.vastuRemedy ? 1 : 0);
        default:
          return 0;
      }
    });

    setFilteredProperties(sorted);
  }, [searchTerm, sortOption, properties]);

  const handleOpenConfirmDialog = (property: Property) => {
    setSelectedProperty(property);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedProperty(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedProperty) {
      try {
        await deletePropertyById(selectedProperty.id);
        handleCloseConfirmDialog();
        useGetAllProperties(currentUser?.tenant?.id);
        toast.success("Property deleted successfully!");
      } catch (error: any) {
        console.log(error);
        toast.error(error?.body?.error?.message);
      }
    }
  };

  const handleOpenCreateDialog = () => setOpenCreateDialog(true);
  const handleCloseCreateDialog = () => setOpenCreateDialog(false);

  const handleEditMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    property: Property
  ) => {
    setEditAnchorEl(event.currentTarget);
    setSelectedPropertyForEdit(property);
  };

  const handleEditMenuClose = () => {
    setEditAnchorEl(null);
    setSelectedPropertyForEdit(null);
  };

  const handleSelectEditMethod = (method: "gridded" | "map" | "site") => {
    handleEditMenuClose();
    if (selectedPropertyForEdit) {
      if (method === "gridded") {
        navigate(`/analysis/${selectedPropertyForEdit.id}`);
      } else if (method === "map") {
        navigate(`/home/${selectedPropertyForEdit.id}`, {
          state: {
            latitude: selectedPropertyForEdit.latitude,
            longitude: selectedPropertyForEdit.longitude,
          },
        });
      } else if (method === "site") {
        navigate(`/site-plan/${selectedPropertyForEdit.id}`);
      }
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSortChange = (event: SelectChangeEvent<SortOption>) => {
    setSortOption(event.target.value as SortOption);
  };

  // Consistent color scheme
  // const PRIMARY_COLOR = theme.palette.primary.main;
  const BACKGROUND_COLOR = "#f9fafb";
  const CARD_BACKGROUND = "#ffffff";
  const TEXT_PRIMARY = "#1a2027";
  const TEXT_SECONDARY = "#637381";

  // Font sizes
  const FONT_SIZES = {
    pageTitle: { desktop: "2rem", tablet: "1.75rem", mobile: "1.5rem" },
    cardTitle: { desktop: "1.25rem", tablet: "1.1rem", mobile: "1rem" },
    bodyText: { desktop: "1rem", tablet: "0.9rem", mobile: "0.875rem" },
    buttonText: { desktop: "0.9rem", tablet: "0.85rem", mobile: "0.8rem" },
    smallText: { desktop: "0.875rem", tablet: "0.8rem", mobile: "0.75rem" },
  };

  // Get the appropriate font size based on device
  const getFontSize = (type: keyof typeof FONT_SIZES) => {
    if (isMobile) return FONT_SIZES[type].mobile;
    if (isTablet) return FONT_SIZES[type].tablet;
    return FONT_SIZES[type].desktop;
  };

  if (isLoading) {
    return <FallbackLoader />;
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          sx={{
            background: "linear-gradient(135deg, #f6f8f9 0%, #e5ebee 100%)",
            p: isMobile ? 2 : 4,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            color="error"
            sx={{
              background: "rgba(255,0,0,0.1)",
              padding: theme.spacing(2),
              borderRadius: theme.spacing(2),
              fontSize: getFontSize("bodyText"),
            }}
          >
            Error fetching properties
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      disableGutters={isMobile}
      sx={{
        px: isMobile || isTablet ? 1 : 4,
        py: isMobile ? 2 : 4,
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BACKGROUND_COLOR} 0%, ${theme.palette.primary.light}15 100%)`,
          minHeight: "100vh",
          p: isMobile ? 1 : 2,
          borderRadius: "16px",
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={isMobile || isTablet ? 2 : 4}
          sx={{
            flexDirection: isMobile || isTablet ? "column" : "row",
            gap: isMobile ? 2 : 0,
            textAlign: isMobile || isTablet ? "center" : "left",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{
              flexDirection: isMobile || isTablet ? "column" : "row",
              mb: isMobile || isTablet ? 2 : 0,
            }}
          >
            <HomeIcon
              sx={{
                fontSize: isMobile || isTablet ? "1.5rem" : "2.5rem",
                color: theme.palette.primary.main,
                mb: isMobile || isTablet ? 1 : 0,
              }}
            />
            <Typography
              variant={isMobile || isTablet ? "h6" : "h4"}
              sx={{
                fontWeight: 600,
                color: TEXT_PRIMARY,
                fontSize: getFontSize("pageTitle"),
              }}
            >
              My Properties
            </Typography>
          </Box>
          <Button
            fullWidth={isMobile || isTablet}
            variant="contained"
            color="primary"
            onClick={handleOpenCreateDialog}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              boxShadow: "0 4px 15px rgba(33, 150, 243, 0.3)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(33, 150, 243, 0.4)",
              },
              maxWidth: isMobile || isTablet ? "100%" : 300,
              margin: isMobile || isTablet ? "0 auto" : "initial",
              fontSize: getFontSize("buttonText"),
              py: isMobile || isTablet ? 1 : 1.5,
            }}
          >
            Create New Property
          </Button>
        </Box>

        {/* Search and Sort Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile || isTablet ? "column" : "row",
            gap: 2,
            mb: 3,
            px: isMobile ? 0.5 : 2,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "12px",
                fontSize: getFontSize("bodyText"),
              },
            }}
            sx={{
              flex: 3,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "rgba(0, 0, 0, 0.1)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0, 0, 0, 0.2)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
          <FormControl sx={{ flex: 1, minWidth: isMobile ? "100%" : 200 }}>
            <InputLabel
              id="sort-select-label"
              sx={{ fontSize: getFontSize("smallText") }}
            >
              Sort By
            </InputLabel>
            <Select
              labelId="sort-select-label"
              value={sortOption}
              onChange={handleSortChange}
              input={
                <OutlinedInput
                  label="Sort By"
                  startAdornment={
                    <InputAdornment position="start">
                      <SortIcon color="action" />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: "12px",
                    fontSize: getFontSize("bodyText"),
                  }}
                />
              }
            >
              <MenuItem
                value="name-asc"
                sx={{ fontSize: getFontSize("bodyText") }}
              >
                Name (A-Z)
              </MenuItem>
              <MenuItem
                value="name-desc"
                sx={{ fontSize: getFontSize("bodyText") }}
              >
                Name (Z-A)
              </MenuItem>
              <MenuItem
                value="date-asc"
                sx={{ fontSize: getFontSize("bodyText") }}
              >
                Date (Oldest First)
              </MenuItem>
              <MenuItem
                value="date-desc"
                sx={{ fontSize: getFontSize("bodyText") }}
              >
                Date (Newest First)
              </MenuItem>
              <MenuItem
                value="hasRemedy"
                sx={{ fontSize: getFontSize("bodyText") }}
              >
                Has Vastu Remedy
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Results Summary */}
        <Box sx={{ px: isMobile ? 0.5 : 2, mb: 2 }}>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ fontSize: getFontSize("smallText") }}
          >
            {filteredProperties.length}{" "}
            {filteredProperties.length === 1 ? "property" : "properties"} found
            {searchTerm && ` for "${searchTerm}"`}
          </Typography>
        </Box>

        {/* Empty State */}
        {filteredProperties.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{
              height: "60vh",
              textAlign: "center",
              opacity: 0.7,
              p: isMobile ? 2 : 0,
            }}
          >
            <HomeIcon
              sx={{
                fontSize: isMobile ? "2rem" : "5rem",
                color: theme.palette.primary.main,
                mb: 2,
              }}
            />
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{ mb: 2, fontSize: getFontSize("cardTitle") }}
            >
              {searchTerm ? "No Properties Found" : "No Properties Added Yet"}
            </Typography>
            <Typography
              variant={isMobile ? "body2" : "body1"}
              color="textSecondary"
              sx={{
                mb: 3,
                px: isMobile ? 2 : 0,
                fontSize: getFontSize("bodyText"),
              }}
            >
              {searchTerm
                ? "Try adjusting your search criteria or clear the search"
                : "Start by creating your first property"}
            </Typography>
            {!searchTerm && (
              <Button
                fullWidth={isMobile}
                variant="contained"
                color="primary"
                onClick={handleOpenCreateDialog}
                startIcon={<AddIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  maxWidth: isMobile ? "100%" : 300,
                  margin: isMobile ? "0 auto" : "initial",
                  fontSize: getFontSize("buttonText"),
                  py: isMobile ? 1.5 : 2,
                }}
              >
                Add First Property
              </Button>
            )}
            {searchTerm && (
              <Button
                fullWidth={isMobile}
                variant="outlined"
                color="primary"
                onClick={() => setSearchTerm("")}
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  maxWidth: isMobile ? "100%" : 300,
                  margin: isMobile ? "0 auto" : "initial",
                  fontSize: getFontSize("buttonText"),
                  py: isMobile ? 1.5 : 2,
                }}
              >
                Clear Search
              </Button>
            )}
          </Box>
        ) : (
          <Grid
            container
            spacing={isMobile ? 1 : 3}
            sx={{
              px: isMobile ? 0.5 : 2,
            }}
          >
            {filteredProperties.map((property: Property) => (
              <Grid item xs={12} sm={12} md={6} lg={4} key={property.id}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    "&:hover": {
                      transform: isMobile ? "none" : "translateY(-5px)",
                      boxShadow: isMobile
                        ? "0 4px 12px rgba(0,0,0,0.08)"
                        : "0 8px 24px rgba(0,0,0,0.12)",
                    },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: CARD_BACKGROUND,
                    overflow: "hidden",
                  }}
                >
                  {/* Indicator for properties with Vastu Remedy */}
                  {property.vastuRemedy && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        backgroundColor: theme.palette.success.main,
                        color: "#fff",
                        fontSize: "0.65rem",
                        padding: "2px 8px",
                        borderRadius: "0 8px 0 8px",
                        zIndex: 1,
                      }}
                    >
                      Remedy Available
                    </Box>
                  )}
                  <CardContent sx={{ flex: 1, p: isMobile ? 2 : 3 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: "#fff",
                          width: isMobile ? 40 : 56,
                          height: isMobile ? 40 : 56,
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        }}
                      >
                        <HomeIcon
                          sx={{
                            color: "#ffffff",
                            fontSize: isMobile ? "1.5rem" : "2rem",
                          }}
                        />
                      </Avatar>
                      <Box display="flex" gap={1}>
                        <IconButton
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            backgroundColor: theme.palette.grey[100],
                            "&:hover": {
                              backgroundColor: theme.palette.grey[200],
                            },
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => handleOpenConfirmDialog(property)}
                        >
                          <DeleteIcon
                            fontSize={isMobile ? "small" : "medium"}
                            sx={{ color: theme.palette.error.main }}
                          />
                        </IconButton>
                        <IconButton
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            backgroundColor: theme.palette.grey[100],
                            "&:hover": {
                              backgroundColor: theme.palette.grey[200],
                            },
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => {
                            navigate(`/families/${property.id}`);
                          }}
                        >
                          <FamilyRestroomIcon
                            fontSize={isMobile ? "small" : "medium"}
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: TEXT_PRIMARY,
                        fontSize: getFontSize("cardTitle"),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {property.propertyName}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        mb: 1,
                        fontSize: getFontSize("smallText"),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        height: "40px",
                      }}
                    >
                      {property.address}
                    </Typography>

                    {/* Added property date for sorting reference */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 2,
                        color: TEXT_SECONDARY,
                        fontSize: "0.7rem",
                      }}
                    >
                      Added on:{" "}
                      {new Date(property.createdAt).toLocaleDateString()}
                    </Typography>

                    <Stack
                      direction={isMobile ? "column" : "row"}
                      spacing={2}
                      sx={{
                        mt: 2,
                        "& > *": {
                          textTransform: "none",
                          borderRadius: "8px",
                          width: isMobile ? "100%" : "auto",
                          fontSize: getFontSize("buttonText"),
                        },
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: "white",
                          "&:hover": {
                            bgcolor: theme.palette.primary.dark,
                          },
                          py: isMobile ? 1 : 1.5,
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        }}
                        onClick={(event) => handleEditMenuOpen(event, property)}
                      >
                        Edit
                      </Button>

                      <Button
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        disabled={!property.vastuRemedy}
                        sx={{
                          py: isMobile ? 1 : 1.5,
                          borderColor: property.vastuRemedy
                            ? theme.palette.success.main
                            : theme.palette.grey[300],
                          color: property.vastuRemedy
                            ? theme.palette.success.main
                            : theme.palette.grey[500],
                          "&:hover": {
                            borderColor: property.vastuRemedy
                              ? theme.palette.success.dark
                              : theme.palette.grey[400],
                            backgroundColor: property.vastuRemedy
                              ? "rgba(76, 175, 80, 0.04)"
                              : "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                        onClick={() => {
                          if (property.vastuRemedy) {
                            navigate(`/remedy/${property.vastuRemedy.id}`);
                          }
                        }}
                      >
                        Vastu Remedy
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Edit Menu */}
        <Menu
          anchorEl={editAnchorEl}
          open={Boolean(editAnchorEl)}
          onClose={handleEditMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              borderRadius: "12px",
              minWidth: "150px",
              mt: 1,
              "& .MuiMenuItem-root": {
                fontSize: getFontSize("bodyText"),
                py: 1.5,
              },
            },
          }}
        >
          <MenuItem onClick={() => handleSelectEditMethod("gridded")}>
            Gridded Box
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleSelectEditMethod("map")}>Map</MenuItem>
          <Divider></Divider>
          <MenuItem onClick={() => handleSelectEditMethod("site")}>
            Site Plan
          </MenuItem>
        </Menu>

        {/* Dialogs */}
        <CreatePropertyDialog
          open={openCreateDialog}
          onClose={handleCloseCreateDialog}
        />

        <Dialog
          open={confirmDialogOpen}
          onClose={handleCloseConfirmDialog}
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: "16px",
              padding: "8px",
            },
          }}
        >
          <DialogTitle
            id="confirm-dialog-title"
            sx={{
              fontSize: getFontSize("cardTitle"),
              fontWeight: 600,
              pb: 1,
            }}
          >
            Delete Property
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="confirm-dialog-description"
              sx={{ fontSize: getFontSize("bodyText") }}
            >
              Are you sure you want to delete the property{" "}
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  color: TEXT_PRIMARY,
                }}
              >
                {selectedProperty?.propertyName}
              </Typography>
              ?
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  color: theme.palette.error.main,
                  fontSize: getFontSize("smallText"),
                }}
              >
                This action cannot be undone.
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseConfirmDialog}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontSize: getFontSize("buttonText"),
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontSize: getFontSize("buttonText"),
                ml: 1,
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default PropertyListingPage;
